import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { auth } from '@/lib/api';
import { Terminal, ShieldCheck, AlertTriangle, KeyRound } from 'lucide-react';
import MfaEnrollmentWizard from './MfaEnrollmentWizard';

export default function Login() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [oidcProviders, setOidcProviders] = useState([]);
  const [totpRequired, setTotpRequired] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [enrollChallenge, setEnrollChallenge] = useState(null);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotMsg, setForgotMsg] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/oidc/providers`
        );
        setOidcProviders(data?.providers || []);
      } catch (_e) {
        setOidcProviders([]);
      }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setLoading(true);
    try {
      if (mode === 'login') {
        const data = await auth.login(email, password, totpRequired ? totpCode.trim() : undefined);
        if (data?.mfa_enrollment_required) {
          setEnrollChallenge(data.challenge_token);
          setErr('');
          return;
        }
      } else {
        await auth.register(email, password, 'operator');
      }
      navigate('/');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === '2fa_required') {
        setTotpRequired(true);
        setTotpCode('');
        setErr('');
      } else if (detail === '2fa_invalid') {
        setErr('Invalid 2FA code — try a fresh code or a backup code');
      } else {
        setErr(detail || 'Authentication failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const cancelTotp = () => {
    setTotpRequired(false);
    setTotpCode('');
    setUseRecoveryCode(false);
    setErr('');
  };

  const resetToCredentials = () => {
    setEnrollChallenge(null);
    setTotpRequired(false);
    setTotpCode('');
    setUseRecoveryCode(false);
    setErr('');
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setErr(''); setForgotMsg(''); setLoading(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`,
        { email }
      );
      setForgotMsg(data?.message || 'If the account exists, a reset link was sent.');
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(d === 'password_recovery_not_configured'
        ? 'Recovery email service not configured yet — contact your administrator.'
        : d === 'sender_domain_not_verified'
          ? 'Sender domain not verified yet — add the Resend DNS records for apexflowengine.com and retry.'
          : (d || 'Request failed'));
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const oidcLogin = async (providerKey) => {
    setErr('');
    try {
      const redirectAfter = window.location.origin + '/';
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/oidc/initiate`,
        null,
        { params: { provider: providerKey, redirect_uri: redirectAfter } }
      );
      if (data?.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        setErr('OIDC initiate returned no authorization_url');
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'OIDC initiate failed');
    }
  };

  return (
    <div className="login-bg min-h-screen flex items-center justify-center px-4" data-testid="login-page">
      <div className="relative z-10 w-full max-w-md" data-testid="login-card">
        <div className="panel p-8">
          <div className="flex items-center gap-3 mb-6">
            <Terminal className="text-amber" style={{ color: 'var(--accent-amber)' }} size={28} />
            <div>
              <div className="font-sans font-bold text-2xl tracking-tight" style={{ color: 'var(--text-primary)' }}>APEXFLOW</div>
              <div className="label-xs" style={{ color: 'var(--accent-amber)' }}>GOD MODE · v2.0</div>
            </div>
          </div>

          <div className="mb-5 flex gap-1 border border-[color:var(--border)]">
            <button
              className="flex-1 btn-sharp border-0"
              style={{ background: mode === 'login' ? 'var(--accent-amber)' : 'transparent', color: mode === 'login' ? '#000' : 'var(--text-secondary)' }}
              onClick={() => setMode('login')}
              data-testid="tab-login"
              type="button"
            >SIGN IN</button>
            <button
              className="flex-1 btn-sharp border-0"
              style={{ background: mode === 'register' ? 'var(--accent-amber)' : 'transparent', color: mode === 'register' ? '#000' : 'var(--text-secondary)' }}
              onClick={() => setMode('register')}
              data-testid="tab-register"
              type="button"
            >REGISTER</button>
          </div>

          {enrollChallenge ? (
            <MfaEnrollmentWizard
              challengeToken={enrollChallenge}
              onComplete={() => navigate('/')}
              onCancel={resetToCredentials}
            />
          ) : forgotMode ? (
          <form onSubmit={submitForgot} className="space-y-4" data-testid="forgot-form">
            <div className="label-xs" style={{ color: 'var(--accent-amber)' }}>CREDENTIAL RECOVERY</div>
            <div>
              <div className="label-xs mb-2">OPERATOR EMAIL</div>
              <input
                type="email"
                className="input-sharp"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="forgot-email-input"
              />
            </div>
            {forgotMsg && (
              <div className="p-2 border" style={{ borderColor: 'var(--accent-green, #22c55e)', color: 'var(--accent-green, #22c55e)', fontSize: 11 }} data-testid="forgot-success">{forgotMsg}</div>
            )}
            {err && (
              <div className="flex items-center gap-2 p-2 border" style={{ borderColor: 'var(--accent-red)', color: 'var(--accent-red)', fontSize: 11 }} data-testid="forgot-error">
                <AlertTriangle size={14} /> {err}
              </div>
            )}
            <button type="submit" className="btn-sharp btn-primary w-full" disabled={loading} data-testid="forgot-submit-button">
              {loading ? 'SENDING…' : 'SEND RESET LINK'}
            </button>
            <button
              type="button"
              onClick={() => { setForgotMode(false); setForgotMsg(''); setErr(''); }}
              className="label-xs underline w-full"
              style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}
              data-testid="forgot-back-button"
            >← BACK TO SIGN IN</button>
          </form>
          ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {!totpRequired && (
              <>
                <div>
                  <div className="label-xs mb-2">OPERATOR EMAIL</div>
                  <input
                    type="email"
                    className="input-sharp"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="email-input"
                  />
                </div>
                <div>
                  <div className="label-xs mb-2">CREDENTIAL</div>
                  <input
                    type="password"
                    className="input-sharp"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="password-input"
                  />
                </div>
              </>
            )}
            {totpRequired && (
              <div data-testid="totp-step">
                <div className="flex items-center gap-2 mb-3 p-2 border" style={{ borderColor: 'var(--accent-amber)', color: 'var(--accent-amber)', fontSize: 11 }} data-testid="totp-required-banner">
                  <ShieldCheck size={14} /> TWO-FACTOR AUTHENTICATION REQUIRED · {email}
                </div>
                <div className="label-xs mb-2">{useRecoveryCode ? 'SINGLE-USE RECOVERY CODE' : '6-DIGIT AUTHENTICATOR CODE'}</div>
                <input
                  type="text"
                  inputMode={useRecoveryCode ? 'text' : 'numeric'}
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={16}
                  placeholder={useRecoveryCode ? 'recovery code' : '000000'}
                  className="input-sharp"
                  style={{ letterSpacing: '0.35em', textAlign: 'center', fontSize: 18 }}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))}
                  required
                  data-testid="totp-code-input"
                />
                <div className="mt-2 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={cancelTotp}
                    className="label-xs underline"
                    style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}
                    data-testid="totp-back-button"
                  >← BACK TO CREDENTIALS</button>
                  <button
                    type="button"
                    onClick={() => { setUseRecoveryCode(!useRecoveryCode); setTotpCode(''); setErr(''); }}
                    className="label-xs underline"
                    style={{ color: 'var(--accent-amber)', background: 'none', border: 'none', cursor: 'pointer' }}
                    data-testid="totp-recovery-toggle"
                  >{useRecoveryCode ? 'USE AUTHENTICATOR APP' : 'USE A RECOVERY CODE'}</button>
                </div>
              </div>
            )}
            {err && (
              <div className="flex items-center gap-2 p-2 border" style={{ borderColor: 'var(--accent-red)', color: 'var(--accent-red)', fontSize: 11 }} data-testid="auth-error">
                <AlertTriangle size={14} /> {err}
              </div>
            )}
            <button type="submit" className="btn-sharp btn-primary w-full" disabled={loading || (totpRequired && totpCode.trim().length < 6)} data-testid="submit-button">
              {loading ? 'AUTHENTICATING…' : (totpRequired ? 'VERIFY 2FA CODE' : (mode === 'login' ? 'ENGAGE SESSION' : 'CREATE OPERATOR'))}
            </button>
            {mode === 'login' && !totpRequired && (
              <button
                type="button"
                onClick={() => { setForgotMode(true); setErr(''); }}
                className="label-xs underline w-full"
                style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}
                data-testid="forgot-password-link"
              >FORGOT CREDENTIAL?</button>
            )}
          </form>
          )}

          <div className="my-4 flex items-center gap-2">
            <div className="flex-1" style={{ height: 1, background: 'var(--border)' }} />
            <div className="label-xs" style={{ color: 'var(--text-muted)' }}>OR · ENTERPRISE SSO</div>
            <div className="flex-1" style={{ height: 1, background: 'var(--border)' }} />
          </div>
          <button
            type="button"
            onClick={googleLogin}
            className="btn-sharp w-full flex items-center justify-center gap-2"
            style={{ borderColor: 'var(--border)' }}
            data-testid="google-login-button"
          >
            <svg width="14" height="14" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.1 19 13 24 13c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.3 35 26.8 36 24 36c-5.3 0-9.7-3.4-11.3-8l-6.6 5.1C9.6 39.6 16.3 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.1 4.1-3.9 5.6l6.2 5.2C41 35.2 44 30 44 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
            CONTINUE WITH GOOGLE
          </button>

          {oidcProviders.length > 0 && (
            <div className="mt-2 space-y-1" data-testid="oidc-buttons">
              {oidcProviders.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => oidcLogin(p.key)}
                  className="btn-sharp w-full flex items-center justify-center gap-2"
                  style={{ borderColor: 'var(--accent-amber)', color: 'var(--accent-amber)' }}
                  data-testid={`oidc-login-${p.key}`}
                >
                  <KeyRound size={12} /> CONTINUE WITH {p.label.toUpperCase()}
                </button>
              ))}
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-[color:var(--border)] text-[10px]" style={{ color: 'var(--text-muted)' }}>
            <div className="flex items-center gap-2"><ShieldCheck size={12} /> CLEARANCE · INSTITUTIONAL · SOC2-EQUIVALENT</div>
            <div className="mt-1">Access restricted · named administrator accounts only · MFA enforced</div>
            <div className="mt-2" data-testid="compliance-badge-wrap">
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/public/compliance-badge`}
                 target="_blank" rel="noreferrer"
                 title="Live attestation — audit hash-chain, OFAC screening, KYC readiness">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/public/compliance-badge?format=svg`}
                  alt="ApexFlow live compliance score"
                  data-testid="compliance-badge-img"
                  style={{ height: 22, display: 'inline-block' }}
                />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-4 text-center label-xs" style={{ color: 'var(--text-muted)' }}>
          APEXFLOW ENGINE · UNIFIED VENUE EXECUTION FABRIC
        </div>
      </div>
    </div>
  );
}
